import { FC, Key, useEffect, useRef, useState } from 'react';
import { Alert, Button, Image, Input, Modal, Spin } from 'antd';
import { useMethods } from '@lhb/hook';
import {
  saveChancePointCheckSpotTasks,
  searchChancePointCheckSpotTasks,
} from '@/common/api/expandStore/chancepointCheckSpotTasks';
import V2Table from '@/common/components/Data/V2Table';
import V2ProvinceList from '@/common/components/Form/V2FormProvinceList/V2ProvinceList';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import CreateCheckSpotTaskModal from './CreateCheckSpotTaskModal';
import { CheckSpotTaskOption, CheckSpotTaskRecord } from './checkSpotTaskSelection';
import styles from './RelationCheckSpotTasksModal.module.less';

interface Props {
  record: CheckSpotTaskRecord;
  onCancel: () => void;
  onSuccess: () => void;
}

/** 打开时回显关联并搜索第一页；搜索结果与已选草稿分离，只有父弹窗确定才保存关系。 */
const RelationCheckSpotTasksModal: FC<Props> = ({ record, onCancel, onSuccess }) => {
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<CheckSpotTaskOption[]>([]);
  const [cityPath, setCityPath] = useState<number[]>([]);
  const [tableFilters, setTableFilters] = useState({ keyword: '', cityIds: [] as number[] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const submitting = useRef(false);
  const pageSize = 20;

  const methods = useMethods({
    async loadLinked() {
      setLoading(true);
      setFailed(false);
      const ids = Array.from(new Set(record.checkSpotTaskIds || []));
      try {
        const linked: CheckSpotTaskOption[] = [];
        // 只查询当前关联IDs；空数组绝不能发送，否则会变成无条件任务查询。
        for (let offset = 0; offset < ids.length; offset += pageSize) {
          const batch = ids.slice(offset, offset + pageSize);
          const res = await searchChancePointCheckSpotTasks({ ids: batch, page: 1, size: pageSize });
          if (!Array.isArray(res?.objectList)) throw new Error('Invalid linked tasks response');
          linked.push(...res.objectList.filter(task => batch.includes(task.id)));
        }
        setSelected(Array.from(new Map(linked.map(task => [task.id, task])).values()));
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    changeKeyword(event) {
      setKeyword(event.target.value);
    },
    changeCity(path: number[] = []) {
      setCityPath(path);
      setTableFilters({ keyword: keyword.trim(), cityIds: path.length ? [path[path.length - 1]] : [] });
    },
    searchFirst() {
      setTableFilters(previous => ({ ...previous, keyword: keyword.trim() }));
    },
    async loadTable(params: { keyword: string; cityIds: number[]; page: number; size: number }) {
      const res = await searchChancePointCheckSpotTasks({ keyword: params.keyword, cityIds: params.cityIds, page: params.page, size: params.size });
      if (!Array.isArray(res?.objectList)) throw new Error('Invalid search response');
      return { dataSource: res.objectList, count: res.totalNum };
    },
    changeSelection(keys: Key[], rows: CheckSpotTaskOption[]) {
      const ids = new Set(keys);
      setSelected(previous => Array.from(new Map([...previous, ...rows.filter(Boolean)].filter(task => ids.has(task.id)).map(task => [task.id, task])).values()));
    },
    openCreate() { setCreating(true); },
    closeCreate() { setCreating(false); },
    created(task: CheckSpotTaskOption) {
      setCreating(false);
      setSelected(previous => [...previous, task]);
      // 使用同一公共投影补齐新任务的采集摘要/照片，失败不丢失已创建任务。
      searchChancePointCheckSpotTasks({ ids: [task.id], page: 1, size: 1 }).then((res) => {
        const saved = Array.isArray(res?.objectList) ? res.objectList.find(item => item.id === task.id) : undefined;
        if (saved) setSelected(previous => previous.map(item => item.id === task.id ? saved : item));
      }).catch(() => { /* 保留创建响应，用户仍可提交当前选择。 */ }).finally(() => {
        setTableFilters(previous => ({ ...previous }));
      });
    },
    cancel() {
      if (!submitting.current) onCancel();
    },
    submit() {
      if (loading || failed || submitting.current || creating) return;
      const checkSpotTaskIds = selected.map(task => task.id);
      submitting.current = true;
      setSaving(true);
      let saved = false;
      return saveChancePointCheckSpotTasks({ id: record.id, checkSpotTaskIds }).then((res) => {
        if (res !== true) { V2Message.error('关联失败，请重试'); return; }
        saved = true;
        V2Message.success(checkSpotTaskIds.length ? '关联成功' : '取消关联成功');
      }).catch(() => {
        // 请求层统一提示，保留选择便于重试。
      }).finally(() => {
        submitting.current = false;
        setSaving(false);
        if (saved) onSuccess();
      });
    },
    renderPhoto(value?: string | null) {
      return value ? <Image className={styles.photo} width={36} height={36} src={value} alt='点位图片' /> : '-';
    },
    renderCollection(_value: unknown, task: CheckSpotTaskOption) {
      return [task.collectDate, task.collectorName].filter(Boolean).join('：') || '-';
    },
  });

  useEffect(() => {
    // 回显完成后再挂载表格，避免同一路由的默认查询取消回显请求。
    methods.loadLinked();
  }, [record.id]);

  useEffect(() => {
    if (keyword.trim() === tableFilters.keyword) return;
    const timer = window.setTimeout(methods.searchFirst, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, tableFilters.keyword]);

  return <>
    <Modal open title='关联踩点任务' width={760} maskClosable={false} onCancel={methods.cancel} onOk={methods.submit}
      confirmLoading={saving} okButtonProps={{ disabled: loading || failed || creating }} cancelButtonProps={{ disabled: saving }} closable={!saving} keyboard={!saving}>
      <div className={styles.container}>
        <div className={styles.search}>
          <V2ProvinceList type={2} className={styles.city} placeholder='城市' value={cityPath} onChange={methods.changeCity} showSearch allowClear disabled={loading || saving || failed} />
          <Input.Search placeholder='搜索商场 / 点位 / 任务名称' value={keyword} disabled={loading || saving || failed} allowClear onChange={methods.changeKeyword} onSearch={methods.searchFirst} />
          <Button type='primary' disabled={loading || failed || saving} onClick={methods.openCreate}>＋ 新增踩点任务</Button>
        </div>
        <Spin spinning={loading}>
          {failed ? <Alert type='error' message='关联任务加载失败，请重试后再修改' action={<Button onClick={methods.loadLinked}>重试</Button>} /> : <>
            {!loading && <V2Table
              rowKey='id'
              emptyRender={selected.length ? true : <Button type='link' disabled={saving} onClick={methods.openCreate}>没有对应任务？去新增踩点任务→</Button>}
              pagination={true}
              pageSize={pageSize}
              paginationDisabled={saving}
              hideColumnPlaceholder={true}
              filters={tableFilters}
              onFetch={methods.loadTable}
              scroll={{ x: 660, y: 320 }}
              defaultColumns={[
                { key: 'projectName', title: '项目名称', width: 180, importWidth: true, ellipsis: true },
                { key: 'spotName', title: '点位名称', width: 140, importWidth: true, ellipsis: true },
                { key: 'mainEntrancePic', title: '点位图片', width: 90, importWidth: true, render: methods.renderPhoto },
                { key: 'collectDate', title: '采集日期', width: 210, importWidth: true, render: methods.renderCollection },
              ]}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selected.map(task => task.id),
                preserveSelectedRowKeys: true,
                onChange: methods.changeSelection,
                getCheckboxProps: () => ({ disabled: saving || loading }),
              }}
            />}
          </>}
        </Spin>
      </div>
      {creating && <CreateCheckSpotTaskModal onCancel={methods.closeCreate} onCreated={methods.created} />}
    </Modal>
  </>;
};

export default RelationCheckSpotTasksModal;
